import * as React from "react";
import { twMerge } from "tailwind-merge";
import TextField from "./TextField";

export type OtpInputFieldProps = {
  count?: number;
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

function clampCount(count: number) {
  if (!Number.isFinite(count)) return 1;
  return Math.max(1, Math.min(12, Math.floor(count)));
}

function onlyAlphaNum(s: string) {
  return (s ?? "").replace(/[^a-z0-9]/gi, "");
}

export default function OtpInputField({
  count = 6,
  value,
  onChange,
  className,
}: OtpInputFieldProps) {
  const n = clampCount(count);
  const digits = React.useMemo(() => {
    const cleaned = onlyAlphaNum(value).slice(0, n).split("");
    const arr = Array.from({ length: n }, (_, i) => cleaned[i] ?? "");
    return arr;
  }, [n, value]);
  const inputRefs = React.useRef<Array<HTMLInputElement | null>>([]);

  React.useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, n);
  }, [n]);

  const focusIndex = React.useCallback((idx: number) => {
    inputRefs.current[idx]?.focus();
    inputRefs.current[idx]?.select();
  }, []);

  const setDigitsAt = React.useCallback(
    (updater: (prev: string[]) => string[]) => {
      const next = updater(digits).map((d) => onlyAlphaNum(d).slice(0, 1));
      onChange(next.join(""));
    },
    [digits, onChange],
  );

  const setFromString = React.useCallback(
    (startIndex: number, raw: string) => {
      const nextDigits = [...digits];
      const chars = onlyAlphaNum(raw)
        .slice(0, n - startIndex)
        .split("");
      chars.forEach((ch, i) => {
        nextDigits[startIndex + i] = ch;
      });
      onChange(nextDigits.join(""));

      const nextFocus = Math.min(n - 1, startIndex + chars.length);
      focusIndex(nextFocus);
    },
    [digits, focusIndex, n, onChange],
  );

  const handleChange = React.useCallback(
    (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = onlyAlphaNum(e.currentTarget.value);
      if (!v) {
        setDigitsAt((prev) => {
          const next = [...prev];
          next[i] = "";
          return next;
        });
        return;
      }

      if (v.length === 1) {
        setDigitsAt((prev) => {
          const next = [...prev];
          next[i] = v;
          return next;
        });
        if (i < n - 1) focusIndex(i + 1);
        return;
      }

      // If user pastes into a single input via onChange
      setFromString(i, v);
    },
    [focusIndex, n, setDigitsAt, setFromString],
  );

  const handleKeyDown = React.useCallback(
    (i: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        e.key.length === 1 &&
        /[a-z0-9]/i.test(e.key) &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        // Overwrite even when already filled (maxLength would otherwise block).
        e.preventDefault();
        const ch = e.key;
        setDigitsAt((prev) => {
          const next = [...prev];
          next[i] = ch;
          return next;
        });
        if (i < n - 1) focusIndex(i + 1);
        return;
      }

      if (e.key === "Backspace") {
        if (digits[i]) {
          setDigitsAt((prev) => {
            const next = [...prev];
            next[i] = "";
            return next;
          });
          return;
        }
        if (i > 0) {
          focusIndex(i - 1);
          setDigitsAt((prev) => {
            const next = [...prev];
            next[i - 1] = "";
            return next;
          });
        }
      }
      if (e.key === "ArrowLeft" && i > 0) focusIndex(i - 1);
      if (e.key === "ArrowRight" && i < n - 1) focusIndex(i + 1);
    },
    [digits, focusIndex, n, setDigitsAt],
  );

  const handlePaste = React.useCallback(
    (i: number) => (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData("text");
      const v = onlyAlphaNum(text);
      if (!v) return;
      e.preventDefault();
      setFromString(i, v);
    },
    [setFromString],
  );

  return (
    <div
      className={twMerge(
        "flex items-center justify-center gap-[12px]",
        className,
      )}
    >
      {Array.from({ length: n }, (_, i) => (
        <TextField
          key={i}
          maxLength={1}
          inputMode="text"
          className="w-full"
          pattern="[A-Za-z0-9]*"
          value={digits[i] ?? ""}
          onPaste={handlePaste(i)}
          onChange={handleChange(i)}
          onKeyDown={handleKeyDown(i)}
          aria-label={`OTP digit ${i + 1}`}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          classNames={{
            label: "hidden",
            container: "h-[50px] rounded-[6px]",
            input:
              "text-center text-[18px] font-semibold placeholder:text-text-secondary-light",
          }}
          ref={(el) => {
            inputRefs.current[i] = el;
          }}
        />
      ))}
    </div>
  );
}
